// Vendor shorts (decision 176): what a vendor will not bring, from its invoice, what it said, or the dock, with the
// stores' orders it reaches, the text to send each store and its morning to-do. On the dashboard (the open ones) and on
// the order day (the load's, with "the vendor said" to type a call or a text in). The app flags; a person changes the
// orders on the invoice check (JL: "just flag it").
import type { VendorShort } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth';
import { formatDate, formatQty, parseTyped } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { ErrorBox } from './ui';

function ShortRow({ s, canAct, showLoad, onDone }: { s: VendorShort; canAct: boolean; showLoad: boolean; onDone: () => void }) {
  const { t, intlLocale } = useLocale();
  const [copied, setCopied] = useState<string | null>(null);
  const done = useMutation({ mutationFn: (v: boolean) => api.vendorShortDone(s.id, v), onSuccess: onDone });
  const untype = useMutation({
    mutationFn: () => api.typeVendorShort({ vendorId: s.vendorId, date: s.loadDate, ...(s.loadWeekday ? { load: s.loadWeekday } : {}), itemId: s.itemId, qty: null }),
    onSuccess: onDone,
  });
  const copy = async (customerId: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(customerId);
    } catch {
      // the clipboard is blocked: the text is on the store's to-do on Follow-ups too
      setCopied(null);
    }
  };
  return (
    <li className="vshort" data-testid="vendor-short">
      <p className="vshort-head">
        <strong>{s.itemName}</strong>{' '}
        <span className="bad-text">{t('vshortComing').replace('{vendor}', s.vendorName).replace('{coming}', formatQty(s.coming)).replace('{ordered}', formatQty(s.ordered))}</span>
        <span className="muted">
          {' · '}
          {/* the invoice it was read from: a paper from the mailbox is checked before the stores are told */}
          {s.source === 'invoice' && s.documentId && canAct ? <Link to={`/costs/${s.documentId}`}>{t(`vshortFrom_${s.source}`)}</Link> : t(`vshortFrom_${s.source}`)} · {t('vshortOnHand').replace('{n}', formatQty(s.onHand))}
          {showLoad ? ` · ${t('vshortLoad').replace('{day}', formatDate(s.loadDate, intlLocale, 'day'))}` : ''}
        </span>
        {canAct ? (
          <span className="vshort-acts">
            {s.source === 'typed' ? (
              <button type="button" className="btn btn-ghost btn-sm" disabled={untype.isPending} onClick={() => { if (window.confirm(`${t('vshortUndoTyped')}: ${t('confirmSure')}`)) untype.mutate(); }}>
                {t('vshortUndoTyped')}
              </button>
            ) : null}
            <button type="button" className="btn btn-ghost btn-sm" disabled={done.isPending} onClick={() => done.mutate(!s.doneAt)}>
              {s.doneAt ? t('vshortReopen') : t('vshortHandled')}
            </button>
          </span>
        ) : null}
      </p>
      {s.stores.length === 0 ? (
        <p className="muted small">{t('vshortNoStores')}</p>
      ) : (
        <ul className="vshort-stores">
          {s.stores.map((x) => (
            <li key={`${x.orderId}`}>
              <Link to={`/customers/${x.customerId}`}>{x.customerName}</Link> {formatQty(x.qty)}
              <span className="muted">
                {' · '}
                {x.invoiceId ? (
                  <Link to={`/invoices/${x.invoiceId}`}>{t('vshortInvoiceNo').replace('{n}', String(x.invoiceNo))}</Link>
                ) : (
                  t('vshortOrderNo').replace('{n}', String(x.orderNo))
                )}
                {' · '}
                {formatDate(x.requestedDate, intlLocale, 'day')}
                {x.taskId ? ` · ${x.taskDone ? t('vshortTold') : t('vshortToDo')}` : ''}
              </span>{' '}
              <button type="button" className="btn btn-ghost btn-sm" title={x.message} onClick={() => void copy(x.customerId, x.message)}>
                {copied === x.customerId ? t('vshortCopied') : t('vshortCopy')}
              </button>
            </li>
          ))}
        </ul>
      )}
      {done.isError ? <ErrorBox error={done.error} /> : null}
      {untype.isError ? <ErrorBox error={untype.error} /> : null}
    </li>
  );
}

/** A call or a text from the vendor: how many are coming of an item on its placed order for the load. */
function VendorSaid({ date, load, onSaved }: { date: string; load: number; onSaved: () => void }) {
  const { t } = useLocale();
  const sheets = useQuery({ queryKey: qk.vendorSheets(`${date}:${load}`), queryFn: ({ signal }) => api.orderDayVendors({ date, load }, signal) });
  const [pick, setPick] = useState('');
  const [qty, setQty] = useState('');
  // the placed orders' lines: a draft is not coming yet
  const options = (sheets.data?.vendors ?? [])
    .filter((v) => v.status === 'sent' || v.status === 'confirmed')
    .flatMap((v) => v.lines.filter((l) => (l.ordered ?? 0) > 0).map((l) => ({ key: `${v.vendorId}:${l.itemId}`, label: `${l.itemName} · ${v.vendorName} (${formatQty(l.ordered ?? 0)})` })));
  const n = parseTyped(qty);
  const save = useMutation({
    mutationFn: () => {
      const [vendorId, itemId] = pick.split(':') as [string, string];
      return api.typeVendorShort({ vendorId, date, load, itemId, qty: n! });
    },
    onSuccess: () => {
      setPick('');
      setQty('');
      onSaved();
    },
  });
  if (options.length === 0) return null;
  return (
    <form
      className="vshort-said"
      onSubmit={(e) => {
        e.preventDefault();
        if (pick && n !== null && n >= 0) save.mutate();
      }}
    >
      <span className="muted">{t('vshortSaidTitle')}</span>
      <select aria-label={t('vshortSaidItem')} value={pick} onChange={(e) => setPick(e.target.value)}>
        <option value="">{t('vshortSaidItem')}</option>
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </select>
      <input className="qty-input" inputMode="decimal" aria-label={t('vshortSaidQty')} placeholder={t('vshortSaidQty')} value={qty} onChange={(e) => setQty(e.target.value)} />
      <button type="submit" className="btn btn-sm" disabled={!pick || n === null || n < 0 || save.isPending}>
        {t('vsSave')}
      </button>
      {save.isError ? <ErrorBox error={save.error} /> : null}
    </form>
  );
}

/**
 * The open shorts (the dashboard), or a load's (the order day, with the vendor said form). Nothing at all when there is
 * nothing short and nothing to type.
 */
export function VendorShorts({ date, load }: { date?: string; load?: number }) {
  const { t } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const canAct = me?.role === 'owner' || me?.role === 'office';
  // who sells sees them too; the warehouse and drivers do not (the route refuses them)
  const canSee = canAct || me?.role === 'sales';
  const key = date ? `${date}:${load ?? ''}` : 'open';
  const shorts = useQuery({ queryKey: qk.vendorShorts(key), queryFn: ({ signal }) => api.vendorShorts({ ...(date ? { date } : {}), ...(load ? { load } : {}) }, signal), refetchInterval: 5 * 60_000, enabled: canSee });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['forecast', 'vendor-shorts'] });
    void qc.invalidateQueries({ queryKey: ['crm'] });
  };
  const list = shorts.data ?? [];
  const open = list.filter((s) => !s.doneAt);
  const handled = list.filter((s) => s.doneAt);
  if (!canSee) return null;
  if (shorts.isError) return <ErrorBox error={shorts.error} />;
  if (list.length === 0 && !(date && load && canAct)) return null;
  return (
    <section className={`vshorts${open.length ? ' has-open' : ''}`} data-testid="vendor-shorts" aria-label={t('vshortTitle')}>
      {list.length ? (
        <>
          <h2>
            ⚠ {t('vshortTitle')} <span className="muted">({open.length})</span>
          </h2>
          <p className="muted small">{t('vshortHint')}</p>
          <ul className="vshort-list">
            {[...open, ...handled].map((s) => (
              <ShortRow key={s.id} s={s} canAct={canAct} showLoad={!date} onDone={refresh} />
            ))}
          </ul>
        </>
      ) : null}
      {date && load && canAct ? <VendorSaid date={date} load={load} onSaved={refresh} /> : null}
    </section>
  );
}
